import React from "react";
import LearnLayout from "@/components/layout/LearnLayout";
import {
  generateArticleSEO,
  generateFAQSchema,
  generateArticleSchema,
} from "@/lib/seo";
import Link from "next/link";
import {
  Users,
  CheckCircle,
  ArrowRight,
  Zap,
  MessageSquare,
  Brain,
} from "lucide-react";
import GuideCallout from "@/components/seo/GuideCallout";
import { useNewUniversity } from "@/lib/university/UniversityProvider";

export default function SpeakLawPillar() {
  const { uni } = useNewUniversity();

  const seo = generateArticleSEO({
    title: "Speak Law: Mastering Oral Legal Reasoning - Durmah",
    description:
      "Legal mastery is spoken as well as written. Learn how to build oral reasoning, seminar confidence, and viva skills step by step.",
    slug: "speak-law",
    path: "/speak-law",
    keywords:
      `legal speaking, oral reasoning, ${uni.lawLabel} tutorials, legal confidence, mooting, viva`,
  });

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: generateArticleSchema({
            headline: "Speak Law: Mastering Oral Legal Reasoning",
            description: seo.description,
            datePublished: "2026-01-27",
            dateModified: "2026-01-27",
          }),
        }}
      />

      <LearnLayout
        title={seo.title}
        description={seo.description}
        slug="speak-law"
        category={["Speaking"]}
        pinnedSlugs={["quiz-me", "durmah-voice-demo"]}
        canonicalUrl={seo.canonical}
      >
        <article className="prose prose-lg max-w-none">
          {/* Hero */}
          <div className="not-prose mb-12 p-8 bg-gradient-to-br from-indigo-950 to-indigo-900 rounded-3xl text-white shadow-xl">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-white/10 rounded-xl backdrop-blur-sm border border-white/20">
                <Users className="w-8 h-8 text-indigo-300" />
              </div>
              <div className="flex-1">
                <h1 className="text-3xl md:text-5xl font-bold text-white drop-shadow">
                  Speak Law
                </h1>
                <h3 className="font-bold text-indigo-200 mb-2">
                  The third pillar of legal mastery: oral reasoning
                </h3>
                <p className="text-xl text-indigo-100 mb-0 opacity-80">
                  For everyone who can write a solid answer but freezes the
                  moment a tutor asks them to say it out loud.
                </p>
              </div>
            </div>
          </div>

          <p className="text-xl font-medium text-gray-700 leading-relaxed border-l-4 border-indigo-200 pl-6 my-10">
            <span className="font-semibold">Durmah principle:</span>{" "}
            Writing can show legal understanding. Explaining a point aloud,
            especially under pressure, can reveal where your understanding
            still needs work.
          </p>

          <h2>Why speaking matters in law school</h2>
          <p>
            Seminars and tutorials are built around demanding, back-and-forth
            sessions that ask you to form a legal argument in real time. Plenty
            of students write strong essays and still feel a wave of impostor
            syndrome the moment they have to contribute out loud. If that is you,
            you are not behind. You just have not practised the spoken half of
            the skill yet.
          </p>

          <p>
            <strong>Speak Law</strong> is our framework for bridging that gap.
            It isn't about being 'loud'. It's about being <em>precise</em>.
          </p>

          <GuideCallout
            title="Put Speak Law into action"
            body="Use Quiz Me for rapid oral prompts, then switch to Durmah Voice when you want full answers and coaching."
            ctaText="Start with Quiz Me"
            ctaHref="/articles/quiz-me"
            icon={Brain}
            variant="purple"
          />

          <h2 id="what-oral-reasoning-needs">What strong oral reasoning is built on</h2>

          <h3>1. The Vocabulary of Law</h3>
          <p>
            Before you can argue a case, you must be comfortable with the
            terminology. Using <em>'obiter'</em>, <em>'ratio'</em>, or{" "}
            <em>'estoppel'</em> should feel as natural as everyday conversation.
            This comes from repetition.
          </p>

          <h3>2. Structuring on the Fly</h3>
          <p>
            Effective legal speaking follows a structured path. Even in a quick
            seminar response, you should be able to state the rule, apply the
            case, and reach a mini-conclusion (<Link href="/insights/irac-legal-writing" className="text-indigo-600 hover:underline">IRAC</Link>) within 30 seconds.
          </p>

          <h3>3. Handling Challenge</h3>
          <p>
            A tutorial leader's job is to test your logic. Speak Law teaches you
            to view a challenge not as a failure, but as a prompt to refine your
            reasoning. Confidence is knowing you have a method to handle the
            unknown.
          </p>

          <div className="not-prose my-12 p-8 bg-indigo-50 rounded-2xl border border-indigo-100">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              A quick self-check before your next seminar
            </h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start gap-3">
                <Zap className="w-4 h-4 text-indigo-500 mt-1 flex-shrink-0" />
                <span>
                  Can I state the material facts of this case without notes?
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Zap className="w-4 h-4 text-indigo-500 mt-1 flex-shrink-0" />
                <span>
                  Can I define the three elements of this test out loud?
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Zap className="w-4 h-4 text-indigo-500 mt-1 flex-shrink-0" />
                <span>
                  Can I articulate the policy reason behind this decision?
                </span>
              </li>
            </ul>
          </div>

          <h2>From Silently Reading to Speaking Law</h2>
          <p>
            The shift is easiest when you can practise in private first. Tools
            like <strong>Durmah Voice</strong> and <strong>Quiz Me</strong> give
            you a low-stakes place to stumble, refine, and get comfortable
            reasoning out loud, so that by the time you are in the room, the
            spoken version already feels familiar.
          </p>
        </article>
      </LearnLayout>
    </>
  );
}
